(ns manage.video-rendering.view
  (:require ["@mui/material/Alert$default" :as Alert]
            ["@mui/material/Box$default" :as Box]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Card$default" :as Card]
            ["@mui/material/CardContent$default" :as CardContent]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Dialog$default" :as Dialog]
            ["@mui/material/DialogActions$default" :as DialogActions]
            ["@mui/material/DialogContent$default" :as DialogContent]
            ["@mui/material/DialogTitle$default" :as DialogTitle]
            ["@mui/material/FormControl$default" :as FormControl]
            ["@mui/material/InputLabel$default" :as InputLabel]
            ["@mui/material/MenuItem$default" :as MenuItem]
            ["@mui/material/Select$default" :as Select]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Tab$default" :as Tab]
            ["@mui/material/Table$default" :as Table]
            ["@mui/material/TableBody$default" :as TableBody]
            ["@mui/material/TableCell$default" :as TableCell]
            ["@mui/material/TableContainer$default" :as TableContainer]
            ["@mui/material/TableHead$default" :as TableHead]
            ["@mui/material/TableRow$default" :as TableRow]
            ["@mui/material/Tabs$default" :as Tabs]
            ["@mui/material/TextField$default" :as TextField]
            ["@mui/material/Typography$default" :as Typography]
            [common.flags :refer [use-flag]]
            [common.patterns.loading-view :refer [loading-view]]
            [common.widgets.page :as page]
            [manage.video-rendering.db :as db]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [defui $]]
            [uix.re-frame :refer [use-subscribe]]))

(defn job-state-chip [state]
  (let [props (case state
                "pending"   {:label "Pending"   :color "default"}
                "ready"     {:label "Ready"     :color "info"}
                "running"   {:label "Running"   :color "warning"}
                "succeeded" {:label "Succeeded" :color "success"}
                "failed"    {:label "Failed"    :color "error"}
                "cancelled" {:label "Cancelled" :color "default"}
                {:label state :color "default"})]
    ($ Chip (assoc props :size "small"))))

(defui render-job-row [{:keys [job on-cancel]}]
  (let [assets (get-in job [:result-data :assets])]
    ($ TableRow {:hover true}
       ($ TableCell (:id job))
       ($ TableCell (:template-id job))
       ($ TableCell (:profile job))
       ($ TableCell (job-state-chip (:state job)))
       ($ TableCell
          (let [error-msg (get-in job [:result-data :cause])]
            (case (:state job)
              "failed" ($ Typography {:variant "body2"
                                      :color "error"
                                      :sx #js {:wordBreak "break-word"}}
                          (or error-msg "Unknown error"))
              "succeeded" (when (seq assets)
                            ($ Stack {:direction "column" :spacing 0.5}
                               (for [[idx asset] (map-indexed vector assets)]
                                 ($ :a {:key idx
                                        :href (:url asset)
                                        :target "_blank"
                                        :style {:color "#2563eb" :textDecoration "underline"}}
                                    (str (:type asset) " " (:width asset) "×" (:height asset))))))
              nil)))
       ($ TableCell
          (when (contains? #{"pending" "ready" "running"} (:state job))
            ($ Button
               {:onClick #(on-cancel (:id job))
                :variant "outlined"
                :size "small"}
               "Cancel"))))))

(defui render-jobs-table [{:keys [jobs on-cancel on-next-page on-prev-page on-first-page has-prev has-next]}]
  ($ Box
     ($ TableContainer
        ($ Table {:size "small"}
           ($ TableHead
              ($ TableRow
                 ($ TableCell "ID")
                 ($ TableCell "Template")
                 ($ TableCell "Profile")
                 ($ TableCell "Status")
                 ($ TableCell "Result")
                 ($ TableCell "Actions")))
           ($ TableBody
              (if (seq jobs)
                (for [job jobs]
                  ($ render-job-row {:key (:id job)
                                     :job job
                                     :on-cancel on-cancel}))
                ($ TableRow
                   ($ TableCell
                      {:colSpan 6
                       :align "center"
                       :sx #js {:py 4 :color "text.secondary"}}
                      "No render jobs found"))))))
     ($ Stack {:direction "row" :spacing 1 :sx #js {:mt 2 :justifyContent "flex-end"}}
        ($ Button
           {:onClick on-first-page
            :disabled (not has-prev)
            :variant "outlined"
            :size "small"}
           "First")
        ($ Button
           {:onClick on-prev-page
            :disabled (not has-prev)
            :variant "outlined"
            :size "small"}
           "Previous")
        ($ Button
           {:onClick on-next-page
            :disabled (not has-next)
            :variant "outlined"
            :size "small"}
           "Next"))))

(defui templates-table [{:keys [templates on-select on-next-page on-prev-page on-first-page has-prev has-next]}]
  ($ Box
     ($ TableContainer
        ($ Table {:size "small"}
           ($ TableHead
              ($ TableRow
                 ($ TableCell "Template ID")
                 ($ TableCell "Revision ID")
                 ($ TableCell "Name")
                 ($ TableCell "Dimensions")
                 ($ TableCell "Type")))
           ($ TableBody
              (if (seq templates)
                (for [template templates]
                  ($ TableRow
                     {:key (:id template)
                      :onClick #(on-select template)
                      :sx #js {:cursor "pointer" :&:hover #js {:backgroundColor "action.hover"}}}
                     ($ TableCell (:template-id template))
                     ($ TableCell (:id template))
                     ($ TableCell (:name template))
                     ($ TableCell (str (:width template) "×" (:height template)))
                     ($ TableCell (:template-type template))))
                ($ TableRow
                   ($ TableCell
                      {:colSpan 5
                       :align "center"
                       :sx #js {:py 4 :color "text.secondary"}}
                      "No templates available"))))))
     ($ Stack {:direction "row" :spacing 1 :sx #js {:mt 2 :justifyContent "flex-end"}}
        ($ Button
           {:onClick on-first-page
            :disabled (not has-prev)
            :variant "outlined"
            :size "small"}
           "First")
        ($ Button
           {:onClick on-prev-page
            :disabled (not has-prev)
            :variant "outlined"
            :size "small"}
           "Previous")
        ($ Button
           {:onClick on-next-page
            :disabled (not has-next)
            :variant "outlined"
            :size "small"}
           "Next"))))

(defui create-render-job-dialog [{:keys [open? template render-profiles on-close on-submit]}]
  (let [placeholders (:placeholders template)
        [form-data set-form-data] (uix/use-state {})
        [selected-profile set-selected-profile] (uix/use-state
                                                 (or (:name (first render-profiles)) "default"))

        handle-change (fn [field-key]
                        (fn [e]
                          (set-form-data (assoc form-data field-key (.. e -target -value)))))

        handle-submit (fn []
                        (let [wrapped-content (reduce-kv
                                               (fn [m k v]
                                                 (assoc m k {:type (get-in placeholders [k :type])
                                                             :value v}))
                                               {}
                                               form-data)]
                          (on-submit wrapped-content selected-profile))
                        (set-form-data {})
                        (set-selected-profile (or (:name (first render-profiles)) "default")))]

    ($ Dialog
       {:open (boolean open?)
        :onClose on-close
        :maxWidth "sm"
        :fullWidth true}
       ($ DialogTitle
          (str "Create Render Job: " (:name template)))
       ($ DialogContent
          ($ Typography {:variant "body2" :color "text.secondary" :sx #js {:mb 2}}
             "Fill in the placeholder values for this template")

          ($ Box {:sx #js {:display "flex" :flexDirection "column" :gap 2 :mt 2}}
             ($ FormControl {:fullWidth true}
                ($ InputLabel {:id "render-profile-label"} "Render Profile")
                ($ Select
                   {:labelId "render-profile-label"
                    :value selected-profile
                    :label "Render Profile"
                    :onChange #(set-selected-profile (.. % -target -value))}
                   (for [profile render-profiles]
                     ($ MenuItem {:key (:name profile) :value (:name profile)}
                        ($ Box
                           ($ Typography {:variant "body1"} (:name profile))
                           ($ Typography {:variant "caption" :color "text.secondary"}
                              (:description profile)))))))

             (for [[field-key field-spec] placeholders]
               ($ TextField
                  {:key (str field-key)
                   :label (name field-key)
                   :fullWidth true
                   :value (get form-data field-key "")
                   :onChange (handle-change field-key)
                   :helperText (str "Type: " (:type field-spec))
                   :required true}))))

       ($ DialogActions
          ($ Button {:onClick on-close} "Cancel")
          ($ Button
             {:onClick handle-submit
              :variant "contained"
              :disabled (not= (count form-data) (count placeholders))}
             "Submit")))))

(defui video-rendering-page []
  (let [jobs (use-subscribe [::db/render-jobs])
        templates (use-subscribe [::db/templates])
        render-profiles (use-subscribe [::db/render-profiles])
        loading? (use-subscribe [::db/loading?])
        error (use-subscribe [::db/error])
        selected-template (use-subscribe [::db/selected-template])
        render-jobs-cursor (use-subscribe [::db/render-jobs-cursor])
        render-jobs-history (use-subscribe [::db/render-jobs-history])
        templates-page (use-subscribe [::db/templates-page])
        templates-total-pages (use-subscribe [::db/templates-total-pages])
        path-params (use-subscribe [:router/path-params])
        advertiser-id (or (:advertiser-id path-params)
                          (when-let [match (re-find #"/advertiser/(\d+)" js/window.location.pathname)]
                            (js/parseInt (second match) 10)))
        [active-tab set-active-tab] (uix/use-state 0)

        handle-tab-change (fn [new-value]
                            (set-active-tab new-value)
                            (.setItem js/localStorage "video-rendering-tab" (str new-value)))]

    (uix/use-effect
     (fn []
       (let [saved (js/parseInt (.getItem js/localStorage "video-rendering-tab") 10)]
         (when (and saved (not (js/isNaN saved)))
           (set-active-tab saved))))
     [])

    (uix/use-effect
     (fn []
       (when advertiser-id
         (rf/dispatch [::db/fetch-render-jobs advertiser-id])
         (rf/dispatch [::db/fetch-templates advertiser-id])
         (rf/dispatch [::db/fetch-render-profiles])))
     [advertiser-id])

    ($ page/page {:sidebar false}
       ($ page/page-header
          {:title "Video Rendering"})

       ($ page/content-padding
          (when error
            ($ Alert {:severity "error" :sx #js {:mb 2}}
               error))

          (if loading?
            ($ loading-view)
            ($ Box
               ($ Tabs
                  {:value active-tab
                   :onChange (fn [_ new-value] (handle-tab-change new-value))}
                  ($ Tab {:label "Render Jobs"})
                  ($ Tab {:label "Templates"}))

               (when (= active-tab 0)
                 ($ Box {:sx #js {:py 2}}
                    ($ render-jobs-table
                       {:jobs jobs
                        :on-cancel #(rf/dispatch [::db/cancel-render-job %])
                        :on-first-page #(rf/dispatch [::db/fetch-render-jobs-first advertiser-id])
                        :on-next-page #(rf/dispatch [::db/fetch-render-jobs-next advertiser-id])
                        :on-prev-page #(rf/dispatch [::db/fetch-render-jobs-prev advertiser-id])
                        :has-next (some? render-jobs-cursor)
                        :has-prev (seq render-jobs-history)})))

               (when (= active-tab 1)
                 ($ Box {:sx #js {:py 2}}
                    ($ templates-table
                       {:templates templates
                        :on-select #(rf/dispatch [::db/select-template %])
                        :on-first-page #(rf/dispatch [::db/fetch-templates-first advertiser-id])
                        :on-next-page #(rf/dispatch [::db/fetch-templates-next advertiser-id])
                        :on-prev-page #(rf/dispatch [::db/fetch-templates-prev advertiser-id])
                        :has-next (< templates-page templates-total-pages)
                        :has-prev (> templates-page 1)}))))))

       (when selected-template
         ($ create-render-job-dialog
            {:open? (some? selected-template)
             :template selected-template
             :render-profiles render-profiles
             :on-close #(rf/dispatch [::db/close-dialog])
             :on-submit (fn [content profile]
                          (rf/dispatch [::db/submit-render-job advertiser-id selected-template content profile]))})))))

(defui video-rendering-root []
  (let [enabled? (use-flag :video-rendering-standalone)]
    (if enabled?
      ($ video-rendering-page)
      ($ page/page {:sidebar false}
         ($ page/page-header
            {:title "Feature Not Available"})
         ($ page/content-padding
            ($ Typography {:variant "body1" :color "text.secondary"}
               "Video rendering feature is not enabled for your account."))))))
